import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import TopPromotionCountDownView from '@/website/components/NewLandingPages/components/topPromotionCountDown/common';
import GoodsInTransitView from '@/website/components/NewLandingPages/goodsInTransit/index';
import GoodsInTransiUSView from '@/website/components/NewLandingPages/goodsInTransit/us';
import { getRequestLanguage } from '@/website/constants/locales';
import { useLocaleContext } from '@/website/contexts/AppContext';
import Layout from '@/website/layouts/newLandingPages';
import { ContentfulPageProps, getContentfulData } from '@/website/lib/getContentfulData';
import { promotionInfoStore } from '@/website/store/promotion/promotionInfoStore';
import { buildCanonicalUrl } from '@/website/util/canonical';
import { getGoodsInTransitWebsiteCrawlerHeadData } from '@/website/util/crawler';
import { AmericanCountryList, goodsInTransitOldCompatibilityKey } from '@jove-frontend/uikit';

const SLUG = 'goods-in-transit';

export async function getServerSideProps(context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<ContentfulPageProps>> {
  const language = getRequestLanguage(context.locale, context.req.headers.cookie);

  return getContentfulData(context, SLUG, {
    metaInfo: {
      title: getGoodsInTransitWebsiteCrawlerHeadData(language)?.title,
      description: getGoodsInTransitWebsiteCrawlerHeadData(language)?.description,
      images: '/goods-in-transit-landing.png',
    },
    useNewLogic: true,
    dataFileName: 'goods-in-transit-landing-data.json',
    activeLanguage: language,
  });
}

export default function GoodsInTransit(props: ContentfulPageProps) {
  const { post, promotion, metadata } = props;
  const router = useRouter();
  const {
    state: { hidePromotionLandingPageHeader, hidePromotionLandingPageFooter },
    actions: { updatePromotionDetailInfo },
  } = promotionInfoStore();
  const { country } = useLocaleContext();

  /**
   * effects
   */
  useEffect(() => {
    updatePromotionDetailInfo(promotion);
  }, [promotion]);

  return (
    <>
      <Head>
        <title>{metadata?.openGraph?.title}</title>
        <meta name="description" content={metadata?.openGraph?.description} />
        <link
          key="canonical"
          rel="canonical"
          href={buildCanonicalUrl(router.asPath, process.env['NX_WEBSITE_URL'], router.locale, router.query.country as string)}
        />
        <meta content={metadata?.openGraph?.title} property="og:title" key="og-title" />
        <meta content={metadata?.openGraph?.description} property="og:description" key="og-description" />
        <meta name="twitter:title" content={metadata?.openGraph?.title} />
        <meta name="twitter:description" content={metadata?.openGraph?.description} />
      </Head>
      <Layout.Wrapper>
        {promotion?.status === 'active' && <TopPromotionCountDownView />}
        {!hidePromotionLandingPageHeader && (
          <Layout.Header customLogoUrl={promotion?.brandingLogo} promotion={promotion} insuranceType={goodsInTransitOldCompatibilityKey} />
        )}
        <Layout.Content containterStyles={{ backgroundColor: promotion?.brandingData?.primary_colour || '#EBEFFE' }}>
          {AmericanCountryList.includes(country) ? (
            <GoodsInTransiUSView post={post} promotion={promotion} />
          ) : (
            <GoodsInTransitView post={post} promotion={promotion} slug={SLUG} />
          )}
        </Layout.Content>
        {!hidePromotionLandingPageFooter && <Layout.Footer />}
      </Layout.Wrapper>
    </>
  );
}
